<script setup lang="ts">
import type { PlayerMe, PlayerParty } from '@/api/public'
import FloatingTooltip from '@/components/FloatingTooltip.vue'
import PlayerAvatar from '@/components/PlayerAvatar.vue'
import {
  playerPartyInviteAvailability,
  playerPartyInviteUnavailableReason,
} from '@/lib/playerSocialPresentation'
import { computed } from 'vue'

type PartyPanelMember = {
  steam_id: string
  persona_name?: string | null
  avatar_url?: string | null
  leader: boolean
}

const props = defineProps<{
  player: PlayerMe
  party: PlayerParty | null
  loading: boolean
  actionPending: boolean
  rosterLocked: boolean
  rosterLockReason: string
  selectedMemberSteamId: string
  messageEnabled: boolean
  messageReason: string
  error: string | null
}>()

const emit = defineEmits<{
  close: []
  invite: []
  disband: []
  leave: []
  select: [steamID: string]
  openProfile: [steamID: string]
  message: [steamID: string, trigger: HTMLElement | null]
  kick: [steamID: string]
  transfer: [steamID: string]
  clearError: []
}>()

const isLeader = computed(() => props.party?.leader_steam_id === props.player.steam_id)
const logicalMembers = computed<PartyPanelMember[]>(() => {
  if (props.party?.members.length) return props.party.members
  return [{
    steam_id: props.player.steam_id,
    persona_name: props.player.persona_name,
    avatar_url: props.player.avatar_url || props.player.avatar_full_url,
    leader: true,
  }]
})
const logicalMemberCount = computed(() => logicalMembers.value.length)
const inviteAvailability = computed(() => playerPartyInviteAvailability(
  props.party,
  props.player.steam_id,
  {
    rosterLocked: props.rosterLocked,
    actionPending: props.actionPending,
  },
))
const canInvite = computed(() => inviteAvailability.value.allowed)
const inviteLabel = computed(() => {
  const label = 'Пригласить в группу'
  const reason = playerPartyInviteUnavailableReason(
    inviteAvailability.value,
    props.rosterLockReason,
  )
  return reason ? `${label} — недоступно: ${reason}` : label
})
const leaderActionReason = computed(() => {
  if (props.actionPending) return 'Дождитесь завершения действия с группой.'
  if (props.rosterLocked) {
    return props.rosterLockReason || 'Состав группы временно заблокирован.'
  }
  return ''
})
const disbandLabel = computed(() => {
  const label = 'Распустить группу'
  return leaderActionReason.value
    ? `${label} — недоступно: ${leaderActionReason.value}`
    : label
})
const leaveLabel = computed(() => {
  const label = 'Покинуть группу'
  if (leaderActionReason.value) {
    return `${label} — недоступно: ${leaderActionReason.value}`
  }
  if (!isLeader.value) return label
  if (logicalMemberCount.value > 1) {
    return `${label} — сначала передайте лидерство другому участнику`
  }
  return `${label} — закройте группу кнопкой «Распустить»`
})

function memberName(member: PartyPanelMember): string {
  return member.persona_name || member.steam_id
}

function memberRole(member: PartyPanelMember): string {
  if (member.steam_id === props.player.steam_id) {
    return member.leader ? 'Вы · лидер' : 'Вы'
  }
  return member.leader ? 'Лидер' : 'Участник'
}

function memberActionsID(member: PartyPanelMember): string {
  return `party-member-actions-${member.steam_id}`
}

function requestMessage(member: PartyPanelMember, event: MouseEvent): void {
  if (!props.messageEnabled) return
  emit(
    'message',
    member.steam_id,
    event.currentTarget instanceof HTMLElement ? event.currentTarget : null,
  )
}

function messageActionLabel(member: PartyPanelMember): string {
  if (props.messageEnabled) return `Написать сообщение игроку ${memberName(member)}`
  return props.messageReason
    ? `Сообщение игроку ${memberName(member)} недоступно: ${props.messageReason}`
    : `Сообщение игроку ${memberName(member)} недоступно`
}

function leaderMemberActionLabel(
  action: 'transfer' | 'kick',
  member: PartyPanelMember,
): string {
  const label = action === 'transfer'
    ? `Сделать ${memberName(member)} лидером`
    : `Исключить ${memberName(member)}`
  return leaderActionReason.value
    ? `${label} — недоступно: ${leaderActionReason.value}`
    : label
}

function requestInvite(): void {
  if (canInvite.value) emit('invite')
}

function requestTransfer(member: PartyPanelMember): void {
  if (isLeader.value && !props.actionPending && !props.rosterLocked) {
    emit('transfer', member.steam_id)
  }
}

function requestKick(member: PartyPanelMember): void {
  if (isLeader.value && !props.actionPending && !props.rosterLocked) {
    emit('kick', member.steam_id)
  }
}

function requestDisband(): void {
  if (props.party && isLeader.value && !props.actionPending && !props.rosterLocked) {
    emit('disband')
  }
}

function requestLeave(): void {
  if (props.party && !isLeader.value && !props.actionPending && !props.rosterLocked) {
    emit('leave')
  }
}
</script>

<template>
  <section
    id="player-party-panel"
    class="player-dock-panel party-panel"
    role="dialog"
    aria-modal="true"
    aria-labelledby="player-party-panel-title"
    tabindex="-1"
  >
    <header class="player-dock-panel-header">
      <div>
        <span>Группа</span>
        <h2 id="player-party-panel-title">
          {{ logicalMemberCount }} / 5 игроков
        </h2>
      </div>
      <button
        class="player-dock-close"
        type="button"
        aria-label="Закрыть панель группы"
        @click="$emit('close')"
      >
        <img src="/shared/assets/x_close_png.png" alt="" aria-hidden="true" />
      </button>
    </header>

    <div v-if="error" class="player-dock-alert" role="alert">
      <span>{{ error }}</span>
      <button type="button" aria-label="Скрыть ошибку" @click="$emit('clearError')">×</button>
    </div>
    <div v-else-if="rosterLockReason" class="player-chat-notice" role="status">
      <span>{{ rosterLockReason }}</span>
    </div>

    <div v-if="loading && !party" class="player-chat-notice" role="status">
      Синхронизация группы…
    </div>

    <ol class="party-member-list" aria-label="Состав группы">
      <li
        v-for="member in logicalMembers"
        :key="member.steam_id"
        class="party-member-row"
        :class="{
          selected: selectedMemberSteamId === member.steam_id,
          self: member.steam_id === player.steam_id,
        }"
      >
        <FloatingTooltip
          class="party-member-profile-tooltip"
          :dismiss-on-click="true"
          :max-width="220"
        >
          <button
            class="party-member-profile"
            type="button"
            :aria-label="`Открыть профиль игрока ${memberName(member)}`"
            :data-party-member-primary-action="
              selectedMemberSteamId === member.steam_id ? 'profile' : undefined
            "
            @click="$emit('openProfile', member.steam_id)"
          >
            <PlayerAvatar
              :name="memberName(member)"
              :avatar-url="member.avatar_url"
              size="sm"
              decorative
            />
          </button>
          <template #content>
            Открыть профиль: {{ memberName(member) }}
          </template>
        </FloatingTooltip>

        <div
          v-if="member.steam_id === player.steam_id"
          class="party-member-select self"
        >
          <div class="party-member-copy">
            <strong>{{ memberName(member) }}</strong>
            <span>{{ memberRole(member) }}</span>
          </div>
        </div>
        <button
          v-else
          class="party-member-select"
          type="button"
          :aria-expanded="selectedMemberSteamId === member.steam_id"
          :aria-controls="memberActionsID(member)"
          :aria-label="`Открыть действия участника: ${memberName(member)}`"
          @click="$emit('select', member.steam_id)"
        >
          <span class="party-member-copy">
            <strong>{{ memberName(member) }}</strong>
            <span>{{ memberRole(member) }}</span>
          </span>
        </button>

        <div
          v-if="
            selectedMemberSteamId === member.steam_id &&
            member.steam_id !== player.steam_id
          "
          :id="memberActionsID(member)"
          class="party-member-actions"
          role="group"
          :aria-label="`Действия с игроком ${memberName(member)}`"
        >
          <FloatingTooltip
            class="party-action-tooltip"
            :dismiss-on-click="messageEnabled"
            :max-width="260"
          >
            <button
              class="party-member-action"
              type="button"
              data-party-member-action="message"
              :aria-disabled="!messageEnabled ? 'true' : undefined"
              :aria-label="messageActionLabel(member)"
              @click="requestMessage(member, $event)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 4h18v13H8l-5 4V4Zm2 2v10.8L7.3 15H19V6H5Z" />
              </svg>
            </button>
            <template #content>{{ messageActionLabel(member) }}</template>
          </FloatingTooltip>

          <FloatingTooltip
            v-if="isLeader"
            class="party-action-tooltip"
            :dismiss-on-click="!actionPending && !rosterLocked"
            :max-width="260"
          >
            <button
              class="party-member-action"
              type="button"
              data-party-member-action="transfer"
              :aria-disabled="actionPending || rosterLocked ? 'true' : undefined"
              :aria-label="leaderMemberActionLabel('transfer', member)"
              @click="requestTransfer(member)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m3 7 5 4 4-7 4 7 5-4-2 12H5L3 7Zm4 10h10l1-6-2.5 2-3.5-5.5L8.5 13 6 11l1 6Zm-1 4v-2h12v2H6Z" />
              </svg>
            </button>
            <template #content>
              {{ leaderMemberActionLabel('transfer', member) }}
            </template>
          </FloatingTooltip>

          <FloatingTooltip
            v-if="isLeader"
            class="party-action-tooltip"
            :dismiss-on-click="!actionPending && !rosterLocked"
            :max-width="260"
          >
            <button
              class="party-member-action danger"
              type="button"
              data-party-member-action="kick"
              :aria-disabled="actionPending || rosterLocked ? 'true' : undefined"
              :aria-label="leaderMemberActionLabel('kick', member)"
              @click="requestKick(member)"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-4.7 0-7 2.3-7 7h8.4a7.4 7.4 0 0 1 1.7-5.1C12 13.6 10.7 13 9 13Zm6 2h7v2h-7v-2Z" />
              </svg>
            </button>
            <template #content>
              {{ leaderMemberActionLabel('kick', member) }}
            </template>
          </FloatingTooltip>
        </div>
      </li>
    </ol>

    <footer
      class="party-general-actions"
      role="group"
      aria-label="Действия с группой"
    >
      <FloatingTooltip
        class="party-action-tooltip"
        :dismiss-on-click="canInvite && !actionPending"
        :max-width="260"
      >
        <button
          class="party-general-action invite"
          type="button"
          data-party-general-action="invite"
          :aria-disabled="!canInvite || actionPending ? 'true' : undefined"
          :aria-label="inviteLabel"
          @click="requestInvite"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 2c-4.7 0-7 2.3-7 7h9.3a7.7 7.7 0 0 1 2-4.8C12.3 13.7 10.9 13 9 13Zm8-1V9h2v3h3v2h-3v3h-2v-3h-3v-2h3Z" />
          </svg>
        </button>
        <template #content>{{ inviteLabel }}</template>
      </FloatingTooltip>

      <FloatingTooltip
        v-if="party && isLeader"
        class="party-action-tooltip"
        :dismiss-on-click="!actionPending && !rosterLocked"
        :max-width="260"
      >
        <button
          class="party-general-action danger"
          type="button"
          data-party-general-action="disband"
          :aria-disabled="actionPending || rosterLocked ? 'true' : undefined"
          :aria-label="disbandLabel"
          @click="requestDisband"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm9 0a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM2 20c0-4.3 2-6.5 5.5-6.5 1.2 0 2.2.3 3 .8A7.8 7.8 0 0 0 9.3 20H2Zm12.2-6.1 2.3 2.3 2.3-2.3 1.4 1.4-2.3 2.3 2.3 2.3-1.4 1.4-2.3-2.3-2.3 2.3-1.4-1.4 2.3-2.3-2.3-2.3 1.4-1.4Z" />
          </svg>
        </button>
        <template #content>{{ disbandLabel }}</template>
      </FloatingTooltip>

      <FloatingTooltip
        v-if="party"
        class="party-action-tooltip"
        :dismiss-on-click="!isLeader && !actionPending && !rosterLocked"
        :max-width="260"
      >
        <button
          class="party-general-action leave"
          type="button"
          data-party-general-action="leave"
          :aria-disabled="
            isLeader || actionPending || rosterLocked ? 'true' : undefined
          "
          :aria-label="leaveLabel"
          @click="requestLeave"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M11 3h9v18h-9v-2h7V5h-7V3Zm1.4 5.6L15.8 12l-3.4 3.4-1.4-1.4 1-1H3v-2h9l-1-1 1.4-1.4Z" />
          </svg>
        </button>
        <template #content>{{ leaveLabel }}</template>
      </FloatingTooltip>
    </footer>
  </section>
</template>
